import { useState } from 'react';
import { ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import { Box, Button, Input, InputGroup, InputRightElement, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import * as CustomersService from 'services/CustomersService';
import { Customer } from 'utils/interfaces';
import { truncateName } from 'utils/common-utils';

interface CustomerSearchSelectProps {
  value: { id: number; fullName: string } | null;
  onChange: (customer: Customer) => void;
  jwtToken?: string;
  onSessionExpired: () => void;
  placeholder?: string;
}

// The customers list (1200+) is server-paginated at a fixed page size regardless of the requested
// pageSize, so it can't be preloaded client-side into a normal <Select>/react-select — this searches
// by ragione sociale on demand instead, same UX already used for the customer picker in the "Nuova
// Scadenza" modal (DeadlinesTable.tsx), reusing CustomersService instead of a duplicated raw fetch.
export default function CustomerSearchSelect({ value, onChange, jwtToken, onSessionExpired, placeholder }: CustomerSearchSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false);

  const search = async (term?: string) => {
    if (!jwtToken) return;
    setIsLoading(true);
    const data = await CustomersService.getAllPaginated(jwtToken, onSessionExpired, { fullName: term, pageSize: 20 });
    if (data) setResults(data.content);
    setHasSearchedOnce(true);
    setIsLoading(false);
  };

  return (
    <Menu placement="bottom-start" isLazy onOpen={() => { if (!hasSearchedOnce) search(); }}>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        variant="outline"
        fontWeight="normal"
        width="100%"
        textAlign="left"
        borderColor="#E2E8F0"
        borderRadius="6px"
        height="40px"
        _hover={{ borderColor: 'gray.300' }}
      >
        {value ? truncateName(value.fullName, 30) : (placeholder || 'Seleziona cliente')}
      </MenuButton>
      <MenuList maxHeight="280px" overflowY="auto" width="350px">
        <Box p={2}>
          <InputGroup size="sm">
            <Input
              placeholder="Cerca per ragione sociale..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') search(searchTerm); }}
            />
            <InputRightElement width="2.5rem">
              <Button h="1.75rem" size="sm" onClick={() => search(searchTerm)}>
                <SearchIcon />
              </Button>
            </InputRightElement>
          </InputGroup>
        </Box>
        {isLoading ? (
          <Text px={3} py={2} fontSize="sm" color="gray.500">Ricerca...</Text>
        ) : results.length === 0 ? (
          <Text px={3} py={2} fontSize="sm" color="gray.500">Nessun cliente trovato</Text>
        ) : (
          results.map(customer => (
            <MenuItem key={customer.id} onClick={() => onChange(customer)}>
              {customer.fullName}
            </MenuItem>
          ))
        )}
      </MenuList>
    </Menu>
  );
}
